import { useCallback, useEffect } from "react";
import closeButton from "../../assets/close-button.png";
import AppImage from "../AppImage/AppImage";
import LoadingSpinner from "../Loading/LoadingSpinner";
import { POKEMON_STATS } from "../../constants/pokemon";
import usePokemonDetails from "../../hooks/usePokemonDetails";
import useLockBodyScroll from "../../hooks/useLockBodyScroll";
import {
  getPokemonSpriteUrl,
  getPrimaryType,
  getStatFillPercentage,
  getStatValue,
} from "../../utils/pokemon";
import "./types-colors.css";
import "./index.css";

function Modal({ pokemonName, setShowModal }) {
  const { pokemon, loading } = usePokemonDetails(pokemonName, {
    enabled: Boolean(pokemonName),
  });

  const hide = useCallback(() => setShowModal(false), [setShowModal]);
  useLockBodyScroll(Boolean(pokemonName));

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") {
        hide();
      }
    };

    window.addEventListener("keydown", handleEsc);

    return () => {
      window.removeEventListener("keydown", handleEsc);
    };
  }, [hide]);

  if (loading) {
    return (
      <div className="modal" role="dialog" aria-modal="true" onClick={hide}>
        <div className="modal-content" onClick={(event) => event.stopPropagation()}>
          <LoadingSpinner ariaLabel="pokemon-modal-loading" />
        </div>
      </div>
    );
  }

  if (!pokemon) {
    return null;
  }

  const primaryType = getPrimaryType(pokemon.types);
  const typeClass = primaryType ? `${primaryType}-div` : "normal-div";

  return (
    <div className="modal" role="dialog" aria-modal="true" onClick={hide}>
      <div className="modal-content" onClick={(event) => event.stopPropagation()}>
        <div className={`${typeClass} div-principal`}>
          <button type="button" className="close-button-wrapper" onClick={hide} aria-label="Close">
            <AppImage alt="close-button" className="close-button" src={closeButton} />
          </button>
          <AppImage
            alt={pokemon.name}
            className="image-original"
            src={getPokemonSpriteUrl(pokemon.id)}
          />
          <div className="div-infos">
            <div className="id-name">
              <p className="poke-id"># {pokemon.id}</p>
              <p className="poke-name">{pokemon.name}</p>
            </div>
            <div className="div-types">
              {pokemon.types?.map((pokemonType) => (
                <p
                  className={`${pokemonType.type.name}-modal poke-types`}
                  key={pokemonType.type.name}
                >
                  {pokemonType.type.name}
                </p>
              ))}
            </div>
          </div>
        </div>
        <div className="div-stats">
          <div className="data">
            <h3>Pokedex Data</h3>
            <p>
              <b>Species:</b> {pokemon.species?.name}
            </p>
            <p>
              <b>Height:</b> {pokemon.height * 10}Cm
            </p>
            <p>
              <b>Weight:</b> {pokemon.weight / 10}Kg
            </p>
            {pokemon.abilities?.map((ability) => (
              <p key={ability.ability.name}>
                <b>Ability:</b> {ability.ability.name}
              </p>
            ))}
            <p>
              <b>Base Experience:</b> {pokemon.base_experience}
            </p>
          </div>
          <div className="stats">
            <h3>Base Stats</h3>
            {POKEMON_STATS.map((stat) => {
              const statValue = getStatValue(pokemon.stats, stat.key);

              return (
                <div className="stats-bar" key={stat.key}>
                  <p className="stats-name">
                    <b>{stat.label}:</b>
                  </p>
                  <p className="stats-numbers">{statValue}</p>
                  <div className="stat-track">
                    <div
                      className="stat-fill"
                      data-testid="stat-fill"
                      style={{
                        width: `${getStatFillPercentage(statValue)}%`,
                        backgroundImage: `linear-gradient(90deg, ${stat.from} 0%, ${stat.to} 100%)`,
                        boxShadow: `0 0 8px ${stat.to}55`,
                      }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Modal;
